/**
 * Centralized Date Utilities and Type Definitions
 *
 * This module provides all date-related types and utility functions used across the application.
 *
 * TYPE USAGE GUIDELINES:
 * - Use `DateRange` for UI components and calendar logic (native Date objects)
 * - Use `DateRangeWithLabel` for date pickers with preset options
 * - Use `TZDateRange` for timezone-aware calculations
 * - Use `APIDateRange` for API requests/responses (string format)
 * - Use `CreativeDateRange` for creative API requests where dates might be undefined
 *
 * @module dateUtils
 */

import { fromZonedTime, toZonedTime, formatInTimeZone } from "date-fns-tz";
import { TZDate } from "@date-fns/tz";
import { TimeInterval } from "@/types";
import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  addMonths,
  subDays,
  differenceInDays,
} from "date-fns";

// ============================================
// TYPE DEFINITIONS
// ============================================

/**
 * UI date range (native Date objects)
 * Use for: Calendar components, date pickers, UI interactions
 */
export interface DateRange {
  from: Date;
  to: Date;
}

/**
 * Date range with label (for presets like "Last 7 days")
 * Use for: Date range selectors with predefined options
 */
export interface DateRangeWithLabel extends DateRange {
  label: TimeInterval;
}

/**
 * Timezone-aware date range with TZDate objects
 * Use for: Internal date calculations with timezone awareness
 */
export interface TZDateRange {
  from: TZDate;
  to: TZDate;
  timezone: string;
}

/**
 * API date range (serialized strings)
 * Use for: API requests/responses
 */
export interface APIDateRange {
  start_date: string; // Format: YYYY-MM-DD
  end_date: string; // Format: YYYY-MM-DD
  timezone?: string;
}

/**
 * API date range with label
 * Use for: Report metadata, saved filters
 */
export interface APIDateRangeWithLabel extends APIDateRange {
  label?: TimeInterval;
}

/**
 * Date range for creative data requests
 * Use for: Creative API requests where dates might be undefined
 */
export interface CreativeDateRange {
  start_date: string | undefined;
  end_date: string | undefined;
}

/**
 * Complete interval range with comparison dates
 * Use for: Analysis graphs with comparison periods
 */
export interface IntervalRange {
  start_date: string;
  end_date: string;
  compare_start_date: string | null;
  compare_end_date: string | null;
  date_range: string;
  compare_date_range: string;
  label: string;
}

// ============================================
// TIMEZONE UTILITIES
// ============================================

/**
 * Applies a date-fns function to a date in a specific timezone
 * @param date - Date to transform
 * @param tz - IANA timezone string
 * @param fn - date-fns function to apply
 * @param options - Additional arguments for the function
 * @returns Transformed date
 */
export const calcZonedDate = (
  date: Date,
  tz: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fn?: (date: Date, options?: any) => Date,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: any = null,
): Date => {
  if (!fn) {
    const inputZoned = toZonedTime(date, tz);
    return fromZonedTime(inputZoned, tz);
  }

  const inputZoned = toZonedTime(date, tz);
  const resultZoned = options !== null ? fn(inputZoned, options) : fn(inputZoned);
  return fromZonedTime(resultZoned, tz);
};

/**
 * Gets timezone display name
 * @param timezone - IANA timezone
 * @returns Human-readable timezone name
 *
 * @example
 * getTimezoneDisplay("America/New_York") // "New York"
 */
export function getTimezoneDisplay(timezone: string): string {
  const parts = timezone.split("/");
  const labelPart = parts[1] ?? parts[0];
  return (labelPart || timezone).replace(/_/g, " ");
}

/**
 * Parses a date-only string (YYYY-MM-DD) as a date in the specified timezone
 * Returns TZDate which maintains timezone information for proper formatting
 *
 * @param dateString - Date string in YYYY-MM-DD format (represents a date in the specified timezone)
 * @param timezone - IANA timezone string (e.g., "America/New_York", "UTC")
 * @returns TZDate object that can be formatted using formatInTimeZone()
 *
 * @example
 * // API returns "2024-01-15" (representing Jan 15 in America/New_York timezone)
 * const tzDate = parseDateAsZoned("2024-01-15", "America/New_York");
 * formatInTimeZone(tzDate, "America/New_York", "MMM dd") // Always shows "Jan 15"
 */
export function parseDateAsZoned(dateString: string, timezone: string): TZDate {
  const tz = timezone || "UTC";
  // Append time component to create a datetime at midnight in the timezone
  const dateTimeString = `${dateString}T00:00:00`;
  // Parse as if this datetime is in the specified timezone, then convert to UTC
  const utcDate = fromZonedTime(dateTimeString, tz);
  // Return TZDate which maintains timezone information
  return new TZDate(utcDate, tz);
}

/**
 * Gets current date/time in specified timezone
 * @param timezone - IANA timezone
 * @returns TZDate representing "now" in that timezone
 */
export function nowInTimezone(timezone: string): TZDate {
  return new TZDate(new Date(), timezone);
}

// ============================================
// PREDEFINED DATE RANGES
// ============================================

/**
 * Gets predefined date ranges for a timezone
 * @param timezone - IANA timezone string
 * @returns Record of TimeInterval to DateRange mappings
 */
export const getPredefinedRanges = (
  timezone: string,
): Record<TimeInterval, DateRange | null> => {
  const now = new Date();
  const nowInUTC = new TZDate(now, "UTC");

  const baseRanges = {
    [TimeInterval.TODAY]: {
      from: new TZDate(calcZonedDate(nowInUTC, timezone, startOfDay), timezone),
      to: new TZDate(calcZonedDate(nowInUTC, timezone, endOfDay), timezone),
    },
    [TimeInterval.YESTERDAY]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(nowInUTC, timezone, subDays, 1),
          timezone,
          startOfDay,
        ),
        timezone,
      ),
      to: new TZDate(
        calcZonedDate(calcZonedDate(nowInUTC, timezone, subDays, 1), timezone, endOfDay),
        timezone,
      ),
    },
    [TimeInterval.THIS_WEEK]: {
      from: new TZDate(calcZonedDate(nowInUTC, timezone, startOfWeek), timezone),
      to: new TZDate(calcZonedDate(nowInUTC, timezone, endOfDay), timezone),
    },
    [TimeInterval.LAST_WEEK]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(nowInUTC, timezone, addDays, -7),
          timezone,
          startOfWeek,
        ),
        timezone,
      ),
      to: new TZDate(
        calcZonedDate(
          calcZonedDate(nowInUTC, timezone, addDays, -7),
          timezone,
          endOfWeek,
        ),
        timezone,
      ),
    },
    [TimeInterval.LAST_7_DAYS]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(nowInUTC, timezone, addDays, -6),
          timezone,
          startOfDay,
        ),
        timezone,
      ),
      to: new TZDate(calcZonedDate(nowInUTC, timezone, endOfDay), timezone),
    },
    [TimeInterval.LAST_14_DAYS]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(nowInUTC, timezone, addDays, -13),
          timezone,
          startOfDay,
        ),
        timezone,
      ),
      to: new TZDate(calcZonedDate(nowInUTC, timezone, endOfDay), timezone),
    },
    [TimeInterval.LAST_28_DAYS]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(nowInUTC, timezone, addDays, -27),
          timezone,
          startOfDay,
        ),
        timezone,
      ),
      to: new TZDate(calcZonedDate(nowInUTC, timezone, endOfDay), timezone),
    },
    [TimeInterval.LAST_30_DAYS]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(nowInUTC, timezone, addDays, -29),
          timezone,
          startOfDay,
        ),
        timezone,
      ),
      to: new TZDate(calcZonedDate(nowInUTC, timezone, endOfDay), timezone),
    },
    [TimeInterval.LAST_90_DAYS]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(nowInUTC, timezone, addDays, -89),
          timezone,
          startOfDay,
        ),
        timezone,
      ),
      to: new TZDate(calcZonedDate(nowInUTC, timezone, endOfDay), timezone),
    },
    [TimeInterval.THIS_MONTH]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(nowInUTC, timezone, startOfMonth),
          timezone,
          startOfDay,
        ),
        timezone,
      ),
      to: new TZDate(calcZonedDate(nowInUTC, timezone, endOfDay), timezone),
    },
    [TimeInterval.LAST_MONTH]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(
            calcZonedDate(nowInUTC, timezone, addMonths, -1),
            timezone,
            startOfMonth,
          ),
          timezone,
          startOfDay,
        ),
        timezone,
      ),
      to: new TZDate(
        calcZonedDate(
          calcZonedDate(
            calcZonedDate(nowInUTC, timezone, addMonths, -1),
            timezone,
            endOfMonth,
          ),
          timezone,
          endOfDay,
        ),
        timezone,
      ),
    },
    [TimeInterval.LAST_3_MONTHS]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(
            calcZonedDate(nowInUTC, timezone, addMonths, -3),
            timezone,
            startOfMonth,
          ),
          timezone,
          startOfDay,
        ),
        timezone,
      ),
      to: new TZDate(
        calcZonedDate(
          calcZonedDate(
            calcZonedDate(nowInUTC, timezone, addMonths, -1),
            timezone,
            endOfMonth,
          ),
          timezone,
          endOfDay,
        ),
        timezone,
      ),
    },
    [TimeInterval.LAST_12_MONTHS]: {
      from: new TZDate(
        calcZonedDate(
          calcZonedDate(
            calcZonedDate(nowInUTC, timezone, addMonths, -12),
            timezone,
            startOfMonth,
          ),
          timezone,
          startOfDay,
        ),
        timezone,
      ),
      to: new TZDate(
        calcZonedDate(
          calcZonedDate(
            calcZonedDate(nowInUTC, timezone, addMonths, -1),
            timezone,
            endOfMonth,
          ),
          timezone,
          endOfDay,
        ),
        timezone,
      ),
    },
    [TimeInterval.CUSTOM]: null,
    [TimeInterval.AUTO]: null,
  };

  return Object.fromEntries(
    Object.entries(baseRanges).map(([key, range]) => [key, range]),
  ) as Record<TimeInterval, DateRange | null>;
};

// ============================================
// DATE FORMATTING
// ============================================

/**
 * Formats a date to API format (YYYY-MM-DD)
 * @param date - Date or TZDate to format
 * @param timezone - Optional timezone (required if date is a regular Date object)
 * @returns Date string in format YYYY-MM-DD
 *
 * @example
 * formatForAPI(tzDate) // Uses TZDate's timezone
 * formatForAPI(date, "America/New_York") // Uses specified timezone
 */
export function formatForAPI(date: TZDate | Date, timezone?: string): string {
  if (date instanceof TZDate) {
    return date.toLocaleString("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      timeZone: date.timeZone,
    });
  }

  // For regular Date objects, timezone is required
  const tz = timezone ?? "UTC";
  return date.toLocaleString("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: tz,
  });
}

/**
 * Formats date range labels for graph x-axis using start_date and end_date
 * @param startDate - Start date string in YYYY-MM-DD format (in account timezone)
 * @param endDate - End date string in YYYY-MM-DD format (in account timezone)
 * @param interval - The interval type (e.g., 'monthly', 'weekly', 'daily')
 * @param timezone - The account timezone (e.g., 'America/New_York', 'UTC')
 * @returns Formatted date range string
 */
export const formatGraphDateLabel = (
  startDate: string,
  endDate: string,
  interval?: string,
  timezone?: string,
): string => {
  try {
    const tz = timezone || "UTC";
    // Parse date strings (YYYY-MM-DD) as TZDate in the account timezone
    // The dates from API are already in account timezone format as date strings
    const start = parseDateAsZoned(startDate, tz);
    const end = parseDateAsZoned(endDate, tz);

    // For monthly intervals, show only the month name regardless of year
    if (interval === "monthly") {
      return formatInTimeZone(start, tz, "MMM");
    }

    // Check if it's the same day
    if (differenceInDays(end, start) === 0) {
      return formatInTimeZone(start, tz, "MMM dd");
    }

    // Check if it's the same month and same year
    const startMonth = formatInTimeZone(start, tz, "MMM");
    const startDay = formatInTimeZone(start, tz, "dd");
    const endMonth = formatInTimeZone(end, tz, "MMM");
    const endDay = formatInTimeZone(end, tz, "dd");
    const startYear = parseInt(formatInTimeZone(start, tz, "yyyy"));
    const endYear = parseInt(formatInTimeZone(end, tz, "yyyy"));

    if (startMonth === endMonth && startYear === endYear) {
      return `${startMonth} ${startDay} - ${endDay}`;
    }

    // Check if it's the same year
    if (startYear === endYear) {
      return `${formatInTimeZone(start, tz, "MMM dd")} - ${formatInTimeZone(end, tz, "MMM dd")}`;
    }

    // Different years - use YY format for more concise labels
    return `${formatInTimeZone(start, tz, "MMM dd, yy")} - ${formatInTimeZone(end, tz, "MMM dd, yy")}`;
  } catch (error) {
    console.error("Error formatting date label:", error);
    return `${startDate} - ${endDate}`;
  }
};

/**
 * Formats a date range for display
 * @param date - Object with start_date, end_date, and timezone
 * @returns Human-readable date range string
 */
export const formatDateRange = (date?: {
  start_date?: string | Date;
  end_date?: string | Date;
  timezone?: string;
}): string => {
  if (!date?.start_date || !date?.end_date) return "";
  try {
    const tz = date.timezone || "UTC";
    const toTZ = (d: string | Date) =>
      d instanceof Date ? new TZDate(d, tz) : new TZDate(fromZonedTime(d, tz), tz);

    const start = toTZ(date.start_date);
    const end = toTZ(date.end_date);

    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);

    const sameYear = start.getFullYear() === end.getFullYear();
    const sameMonth = start.getMonth() === end.getMonth();
    const sameDay = start.getDate() === end.getDate();

    if (sameDay && sameMonth && sameYear) {
      return start.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
    if (sameYear && sameMonth) {
      return `${start.toLocaleString("en-US", { month: "short" })} ${start.getDate()} - ${end.getDate()}, ${end.getFullYear()}`;
    }
    if (sameYear) {
      return `${start.toLocaleString("en-US", { month: "short" })} ${start.getDate()} - ${end.toLocaleString("en-US", { month: "short" })} ${end.getDate()}, ${end.getFullYear()}`;
    }
    const fmt = (d: Date) =>
      d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return `${fmt(start)} - ${fmt(end)}`;
  } catch {
    return "";
  }
};

/**
 * Formats a timestamp as relative time (e.g., "2 hours ago", "Yesterday 3:45 PM")
 * Uses local browser time
 * @param date - Date to format
 * @returns Relative time string
 */
export function formatRelativeTime(date?: Date): string {
  if (!date) {
    return "Invalid date";
  }
  const now: Date = new Date();
  const todayStart: Date = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart: Date = new Date(todayStart);
  yesterdayStart.setDate(todayStart.getDate() - 1);

  const timeDiff: number = now.getTime() - date.getTime();
  const minutesAgo: number = Math.floor(timeDiff / (1000 * 60));

  if (minutesAgo < 1) {
    return "just now";
  } else if (minutesAgo < 60) {
    return `${minutesAgo} min ago`;
  } else if (date >= todayStart) {
    const hoursAgo = Math.floor(minutesAgo / 60);
    return hoursAgo === 1 ? "1 hour ago" : `${hoursAgo} hours ago`;
  } else if (date >= yesterdayStart) {
    return (
      "Yesterday " +
      new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }).format(date)
    );
  } else {
    const formattedSpecificDate: string =
      date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        year: "2-digit",
      }) +
      " " +
      new Intl.DateTimeFormat("en-US", {
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      }).format(date);
    return formattedSpecificDate;
  }
}

// ============================================
// DATE RANGE CALCULATIONS
// ============================================

/**
 * Returns a default 7-day date range based on the allowed date range
 * @param allowedDateRange - Object with first and last allowed dates
 * @param timezone - IANA timezone string
 * @returns DateRangeWithLabel or null
 */
export function getDefaultSevenDay(
  allowedDateRange: { first_allowed_date: TZDate; last_allowed_date: TZDate } | null,
  timezone: string = "UTC",
): DateRangeWithLabel | null {
  if (!allowedDateRange) {
    return null;
  }

  const { first_allowed_date, last_allowed_date } = allowedDateRange;
  const now = new TZDate(new Date(), timezone);
  const isLastSyncToday = now.toDateString() === last_allowed_date.toDateString();

  if (isLastSyncToday) {
    const predefinedRanges = getPredefinedRanges(timezone);
    const last7Days = predefinedRanges[TimeInterval.LAST_7_DAYS];
    return last7Days ? { ...last7Days, label: TimeInterval.LAST_7_DAYS } : null;
  } else {
    const endDate = new TZDate(last_allowed_date, timezone);
    let startDate = new TZDate(calcZonedDate(endDate, timezone, subDays, 6), timezone);

    if (startDate < first_allowed_date) {
      startDate = new TZDate(first_allowed_date, timezone);
    }

    return {
      from: startDate,
      to: endDate,
      label: TimeInterval.CUSTOM,
    };
  }
}

/**
 * Gets available intervals for a date range
 * @param startDate - Start date
 * @param endDate - End date
 * @returns Object with available intervals
 */
export const getAvailableIntervals = (startDate: Date, endDate: Date) => {
  const diffDays = Math.ceil(
    (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24),
  );

  return {
    daily: true,
    weekly: diffDays >= 7,
    monthly: diffDays >= 30,
  };
};

/**
 * Determines the most appropriate data aggregation interval for a given date range
 * @param dateRange - Object with start_date and end_date
 * @returns Interval type: "monthly" | "weekly" | "three_day" | "daily"
 */
export const getOptimumInterval = (dateRange: {
  start_date: string | Date;
  end_date: string | Date;
}): string => {
  const startDate = new Date(dateRange.start_date);
  const endDate = new Date(dateRange.end_date);

  const timeDiff = endDate.getTime() - startDate.getTime();
  const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

  if (daysDiff > 90) {
    return "monthly";
  } else if (daysDiff > 21) {
    return "weekly";
  } else if (daysDiff > 9) {
    return "three_day";
  } else {
    return "daily";
  }
};

/**
 * Calculates the difference in days between two dates
 * @param start - Start date
 * @param end - End date
 * @returns Number of days
 */
export function getDaysDifference(start: Date | TZDate, end: Date | TZDate): number {
  return differenceInDays(end, start);
}

// ============================================
// HELPER FUNCTION FOR COMPARE RANGES
// ============================================

/**
 * Computes auto compare range based on main date range
 * @param currentRange - Main date range with label
 * @param timezone - IANA timezone
 * @param first_allowed_date - Optional first allowed date (prevents comparison going before this date)
 * @returns Compare date range with label or null
 */
export const computeAutoCompareRange = (
  currentRange: DateRangeWithLabel,
  timezone: string,
  first_allowed_date?: Date,
): DateRangeWithLabel | null => {
  if (!currentRange?.from || !currentRange?.to) return null;

  const daysDiff = differenceInDays(currentRange.to, currentRange.from) + 1;

  let compareFrom: Date, compareTo: Date;
  if (daysDiff <= 7) {
    compareFrom = calcZonedDate(currentRange.from, timezone, subDays, 7);
    compareTo = calcZonedDate(currentRange.to, timezone, subDays, 7);
  } else {
    compareFrom = calcZonedDate(currentRange.from, timezone, subDays, daysDiff);
    compareTo = calcZonedDate(currentRange.to, timezone, subDays, daysDiff);
  }

  if (
    first_allowed_date &&
    (compareFrom < first_allowed_date || compareTo < first_allowed_date)
  ) {
    compareTo = calcZonedDate(currentRange.from, timezone, subDays, 1);
    if (daysDiff === 1) {
      compareFrom = compareTo;
    } else {
      compareFrom = calcZonedDate(compareTo, timezone, subDays, daysDiff - 1);
    }
    if (compareFrom < first_allowed_date) compareFrom = first_allowed_date;
  }

  if (first_allowed_date && currentRange.from.getTime() === first_allowed_date.getTime())
    return null;

  return { from: compareFrom, to: compareTo, label: TimeInterval.CUSTOM };
};

// ============================================
// RE-EXPORTS FROM date-fns FOR CONVENIENCE
// ============================================

export {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  addDays,
  addMonths,
  subDays,
  differenceInDays,
  TZDate,
};
